import { useEffect, useRef, useState } from "react";
import { PaginatedListResult, usePaginatedList } from "@hooks/usePaginatedList";
import { ServicesURL } from "@utils/utilsApi";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import { EditionRegistryRow } from "../types";

const POLLING_INTERVAL = 5000;

type UpdateStatus = "STARTED" | "COMPLETED" | "ERROR" | null;

interface UseEditionRegistryArgs {
  corporateId: string | number;
  editionId: string | number;
  page: number;
  pageSize: number;
  search?: string;
  fieldFilters?: Record<string, any>;
}

export interface UseEditionRegistryResult extends PaginatedListResult<EditionRegistryRow> {
  isUpdating: boolean;
  updateError: boolean;
}

export const useEditionRegistry = ({
  corporateId,
  editionId,
  page,
  pageSize,
  search,
  fieldFilters,
}: UseEditionRegistryArgs): UseEditionRegistryResult => {
  const [updateStatus, setUpdateStatus] = useState<UpdateStatus>(null);
  const [updateCompleted, setUpdateCompleted] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!corporateId || !editionId) return undefined;

    let cancelled = false;

    const stopPolling = () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };

    const startPolling = () => {
      pollingRef.current = setInterval(async () => {
        try {
          const res: any = await fetcher<any>(
            ServicesURL.getUpdateRegistryStatus
              .replace("{editionId}", String(editionId))
              .replace("{corporateId}", String(corporateId)),
            MethodType.GET,
          );
          const status = res?.status || res?.data?.status;
          if (status === "COMPLETED" || status === "ERROR") {
            if (cancelled) return;
            stopPolling();
            setUpdateStatus(status);
            setUpdateCompleted(true);
          }
        } catch {
          if (cancelled) return;
          stopPolling();
          setUpdateStatus("ERROR");
          setUpdateCompleted(true);
        }
      }, POLLING_INTERVAL);
    };

    const start = async () => {
      setUpdateStatus("STARTED");
      try {
        await fetcher<any>(
          ServicesURL.postUpdateRegistryData
            .replace("{editionId}", String(editionId))
            .replace("{corporateId}", String(corporateId)),
          MethodType.POST,
        );
        if (cancelled) return;
        startPolling();
      } catch {
        if (cancelled) return;
        setUpdateStatus("ERROR");
        setUpdateCompleted(true);
      }
    };

    start();

    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [corporateId, editionId]);

  const url =
    updateCompleted && corporateId && editionId
      ? `${ServicesURL.getVirtualRegister
          .replace("{corporateId}", String(corporateId))
          .replace("{editionId}", String(editionId))}`
      : null;

  const list = usePaginatedList<EditionRegistryRow>(url, {
    page,
    pageSize,
    search,
    fieldFilters,
  });

  return {
    ...list,
    isLoading: list.isLoading || (!updateCompleted && updateStatus !== "ERROR"),
    isUpdating: !updateCompleted && updateStatus !== "ERROR",
    updateError: updateStatus === "ERROR",
  };
};
